import { Injectable } from '@angular/core';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { FormControl } from '@angular/forms';

import { SNACKBAR } from '@domain/constants/snackbar-setting';
import {
	ADD_DAYS,
	CarrierFeedback,
	DATE_YMD,
	IS_EMPTY,
	IS_LESS_THAN_CURRENT_TIMESTAMP,
	IS_NOT_EMPTY,
	IS_NOT_NULL_OR_UNDEFINED,
	IS_NULL_OR_UNDEFINED,
	IS_VALID_DATE,
	Location,
	ProjectDTO,
	Quote,
	SnackbarService,
	TOMORROW,
} from 'cargo-chief-commons';
import { Router } from '@angular/router';
import { CapacitySearch } from '@domain/interface/capacity-search';
import { Cargochief } from '@service/cargochief/cargochief.service';
import {
	ACCOUNT_TYPE,
	CARGOCHIEF_KEYS,
	CARRIER_FEEDBACK_SETTINGS,
	PLAN_NAMES,
} from '@domain/constants/app-variables';
import { USER_ROLES } from '@domain/constants/user-roles';
import { TRAILER_TYPES_DB } from '@domain/constants/trailer-types';
import { environment } from '@environment/environment';
import {
	GET_CAPACITY_SEARCH_QUERY_PARAMS,
	BUILD_CAPACITY_SEARCH_FROM_PROJECT,
} from '@shared/search-truck-bar/utils';

@Injectable({
	providedIn: 'root',
})
export class UtilService {
	constructor(
		public snackBar: MatSnackBar,
		private snackbarService: SnackbarService,
		private router: Router,
		private cargochief: Cargochief
	) {}

	equalsIgnoreCase(firstString: string, secondString: string): boolean {
		const regex = new RegExp(`^${firstString}$`, 'i');
		return Boolean(secondString.match(regex));
	}

	getTruckSearchObject(truckSearch) {
		let search = Object.assign({}, truckSearch);
		search['pickupDate'] = null;
		search['pickupDate'] = DATE_YMD(truckSearch['pickupDate']['value']);

		return search;
	}

	showSnackbar(
		msg: string,
		className: string,
		action: string,
		duration: number = SNACKBAR.DURATION
	) {
		this.snackbarService.display(msg, SNACKBAR.SNACKBAR_POSITION_TOP, className, '', duration);
	}

	setCapacitySearchQueryParams(search: CapacitySearch, routePath: string) {
		const queryParams = GET_CAPACITY_SEARCH_QUERY_PARAMS(search);
		this.router.navigate([routePath], {
			queryParams: queryParams,
		});
	}

	isAccountTypeBroker(): boolean {
		return this.cargochief.USER_ACCOUNT_TYPE == ACCOUNT_TYPE.BROKER;
	}

	isAccountTypeAdmin(): boolean {
		return this.cargochief.USER_ACCOUNT_TYPE == ACCOUNT_TYPE.ADMIN;
	}

	isAccountTypeShipper(): boolean {
		return this.cargochief.USER_ACCOUNT_TYPE == ACCOUNT_TYPE.SHIPPER;
	}

	isAccountTypeCarrier(): boolean {
		return this.cargochief.USER_ACCOUNT_TYPE == ACCOUNT_TYPE.CARRIER;
	}

	isUserRoleAdmin(): boolean {
		return (
			!this.isNullOrUndefined(this.cargochief.getUser()) &&
			this.cargochief.getUser().role.split(',').indexOf(USER_ROLES.ADMIN) !== -1
		);
	}

	hasPlanProduct(product, planProducts?): boolean {
		return this.cargochief.isProductEnabled(product, planProducts);
	}

	isNullOrUndefined(obj: any) {
		return obj == undefined || obj == null;
	}

	isFullWidthLayout() {
		return !this.isNullOrUndefined(this.cargochief.getLayoutSetting())
			? JSON.parse(this.cargochief.getLayoutSetting())
			: false;
	}

	isActiveClass(route: Array<string>, isToolbar, exactMatch: boolean = false) {
		let isMenuActive = false;
		route.map((path) => {
			if (exactMatch) {
				const currentPath = window.location.href.split('/#/')[1]?.split('?')[0];
				if (currentPath == route[0]) {
					isMenuActive = true;
				}
			} else if (window.location.href.indexOf(path) > 0) {
				isMenuActive = true;
			}
		});
		if (!isToolbar) {
			if (isMenuActive) {
				return 'menu-active';
			} else {
				return 'menu-normal';
			}
		} else {
			if (isMenuActive) {
				return 'toolbar-menu-active';
			} else {
				return 'color-white';
			}
		}
	}

	prependCarrierFeedback(carrierFeedback: CarrierFeedback) {
		if (
			!this.isNullOrUndefined(this.cargochief.getCarrierFeedbackList(carrierFeedback.carrierId))
		) {
			let carrierFeedbackList: CarrierFeedback[] = this.cargochief.getCarrierFeedbackList(
				carrierFeedback.carrierId
			);
			carrierFeedbackList.unshift(carrierFeedback);
			carrierFeedbackList = carrierFeedbackList.slice(0, CARRIER_FEEDBACK_SETTINGS.PAGE_SIZE);
			this.cargochief.setCarrierFeedbackList(carrierFeedback.carrierId, carrierFeedbackList);
		}
	}

	isUserRoleOwner(): boolean {
		return (
			!this.isNullOrUndefined(this.cargochief.getUser()) &&
			!this.isNullOrUndefined(this.cargochief.getUser().role) &&
			this.cargochief.getUser().role.toLowerCase().indexOf(ACCOUNT_TYPE.OWNER.toLowerCase()) > -1
		);
	}

	isValidNumber(num: any): boolean {
		return !/\D/.test(num);
	}

	getUserDataForSegmentEvent(): any {
		return {
			accountId: this.cargochief.ACCOUNT_ID,
			userId: this.cargochief.USER_ID,
			businessName: this.cargochief.BUSINESS_NAME,
			email: this.cargochief.USER_EMAIL,
		};
	}

	navigateToCapacitySearch(projectDTO: ProjectDTO) {
		const search = BUILD_CAPACITY_SEARCH_FROM_PROJECT(projectDTO);
		const queryParams = GET_CAPACITY_SEARCH_QUERY_PARAMS(search);
		this.router.navigate(['capacity'], {
			state: { projectDTO: projectDTO },
			queryParams: queryParams,
		});
	}

	navigateToCapacitySearchFromQuoteDetails(quoteDTO: Quote) {
		const search = this.getSearchObjectFromQuote(quoteDTO);
		this.router.navigate(['capacity'], {
			queryParams: { capacitySearch: JSON.stringify(search) },
		});
	}

	getSearchObjectFromProject(projectDTO: ProjectDTO, resultCount: number = null): CapacitySearch {
		let search = {} as CapacitySearch;
		search.origin = {} as Location;
		search.destination = {} as Location;
		search.origin.city = projectDTO.originCity;
		search.origin.state = projectDTO.originState;
		search.origin.zip = projectDTO.originZip;
		search.origin.latitude = projectDTO.originLat;
		search.origin.longitude = projectDTO.originLng;
		search.destination.city = projectDTO.destCity;
		search.destination.state = projectDTO.destState;
		search.destination.zip = projectDTO.destZip;
		search.destination.latitude = projectDTO.destLat;
		search.destination.longitude = projectDTO.destLng;
		search.trailerType = projectDTO.equipmentType;
		search.pickupDateEnd = DATE_YMD(TOMORROW());
		search.pickupDateStart = DATE_YMD(TOMORROW());
		search.resultCount = resultCount;
		search.hasTeams = projectDTO.requireTeams;
		return search;
	}

	getSearchObjectFromQuote(quoteDTO: Quote) {
		let search = {} as CapacitySearch;
		search.origin = {} as Location;
		search.destination = {} as Location;
		search.origin = quoteDTO.origin;
		search.destination = quoteDTO.destination;
		search.trailerType = quoteDTO.equipment;
		search.pickupDateEnd = DATE_YMD(TOMORROW());
		search.pickupDateStart = DATE_YMD(TOMORROW());
		return search;
	}

	isValidEmail(text: string): boolean {
		const re =
			/^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
		return re.test(text.toLowerCase());
	}

	isValidPhone(text: string): boolean {
		const re = /^\(?(\d{3})\)?[-\. ]?(\d{3})[-\. ]?(\d{4})( x\d{4})?$/;
		return re.test(text);
	}

	isPaidUser(): boolean {
		const account: any = this.cargochief.getAccount(),
			user = this.cargochief.getUser(),
			nextPaymentDate =
				IS_NOT_NULL_OR_UNDEFINED(account) && IS_NOT_NULL_OR_UNDEFINED(account.nextPaymentDate)
					? account.nextPaymentDate
					: null,
			creditCardPayment =
				IS_NOT_NULL_OR_UNDEFINED(account) &&
				IS_NOT_NULL_OR_UNDEFINED(account.creditCardPayment) &&
				!account.creditCardPayment
					? true
					: false;

		if (creditCardPayment) {
			return true;
		}

		if (
			this.isUserLoggedIn(account, user) &&
			IS_NOT_NULL_OR_UNDEFINED(account.planAmount) &&
			(this.isAccountTypeShipper() || this.isAccountTypeBroker() || this.isAccountTypeCarrier()) &&
			(IS_NULL_OR_UNDEFINED(nextPaymentDate) ||
				(IS_NOT_NULL_OR_UNDEFINED(nextPaymentDate) &&
					IS_VALID_DATE(DATE_YMD(nextPaymentDate), ['Y-M-D']) &&
					IS_LESS_THAN_CURRENT_TIMESTAMP(
						DATE_YMD(ADD_DAYS(nextPaymentDate, environment.PAYMENT_TRANSIT_DAYS))
					)))
		) {
			const amountToBePaid = account.planAmount;
			if (IS_NOT_NULL_OR_UNDEFINED(amountToBePaid) && amountToBePaid > 0) {
				this.cargochief.setItem({ [CARGOCHIEF_KEYS.PAYMENT_AMOUNT]: amountToBePaid });
				if (
					IS_NULL_OR_UNDEFINED(this.cargochief.getItem(CARGOCHIEF_KEYS.NEXT_PAYMENT_DATE_EXPIRED))
				) {
					this.cargochief.setItem({ [CARGOCHIEF_KEYS.NEXT_PAYMENT_DATE_EXPIRED]: true });
				}
				return false;
			}
		}
		return true;
	}

	isUserLoggedIn(account: any, user: any): boolean {
		return (
			IS_NOT_NULL_OR_UNDEFINED(account) &&
			IS_NOT_NULL_OR_UNDEFINED(user) &&
			IS_NOT_NULL_OR_UNDEFINED(account.accountType)
		);
	}

	copyToClipboard(text: any, successMsg: string) {
		let selBox = document.createElement('textarea');
		selBox.style.position = 'fixed';
		selBox.style.left = '0';
		selBox.style.top = '0';
		selBox.style.opacity = '0';
		selBox.value = text;
		document.body.appendChild(selBox);
		selBox.focus();
		selBox.select();
		document.execCommand('copy');
		document.body.removeChild(selBox);
		this.showSnackbar(successMsg, SNACKBAR.SUCCESS, SNACKBAR.ACTION);
	}

	isUserRoleUser(): boolean {
		return (
			!this.isNullOrUndefined(this.cargochief.getUser()) &&
			!this.isNullOrUndefined(this.cargochief.getUser().role) &&
			this.cargochief.getUser().role.toLowerCase().indexOf(ACCOUNT_TYPE.USER.toLowerCase()) > -1
		);
	}

	checkIfUserRoleAdminOrOwnerOnly() {
		if (this.isUserRoleAdminOrOwnerOnly()) {
			this.router.navigate([environment.V2_LOADS_PAGE ? 'v2/loadsList' : 'users/myquotes']);
		}
	}

	isUserRoleAdminOrOwnerOnly() {
		if ((this.isUserRoleAdmin() || this.isUserRoleOwner()) && !this.isUserRoleUser()) {
			return true;
		}
		return false;
	}

	isUserRoleEmailEditor(): boolean {
		return (
			!this.isNullOrUndefined(this.cargochief.getUser()) &&
			this.cargochief.getUser().role.split(',').indexOf(USER_ROLES.EMAIL_EDITOR) !== -1
		);
	}

	getAbbreviatedTrailerTypes(trailerType): string {
		if (IS_EMPTY(trailerType)) {
			return;
		}
		switch (trailerType.toLowerCase()) {
			case TRAILER_TYPES_DB.RGN.toLowerCase():
				return TRAILER_TYPES_DB.RGN;
			default:
				return trailerType.slice(0, 1);
		}
	}

	navigateToDefaultViewForUser() {
		if (
			IS_NOT_EMPTY(this.cargochief.getUser().planName) &&
			this.cargochief.getUser().planName.toLowerCase() == PLAN_NAMES.PRICING.toLowerCase()
		) {
			this.router.navigate(['pricing']);
		} else {
			this.router.navigate(['capacity']);
		}
	}

	getUserPlanName(): string {
		const account: any = this.cargochief.getAccount();
		if (IS_NULL_OR_UNDEFINED(account)) {
			return null;
		}
		let plan: string = account.plan;
		if (this.isNullOrUndefined(plan)) {
			plan = account.planDisplayName;
		}
		return plan;
	}

	naviagteToDefaultAccountView() {
		switch (this.cargochief.USER_ACCOUNT_TYPE) {
			case ACCOUNT_TYPE.BROKER:
			case ACCOUNT_TYPE.SHIPPER:
				this.router.navigate(['capacity']);
				break;
			case ACCOUNT_TYPE.CARRIER:
				this.router.navigate(['dashboard']);
				break;
			case ACCOUNT_TYPE.ADMIN:
				this.router.navigate(['admin/accounts']);
				break;
		}
	}

	getStatsTitleSize(title: string): number {
		if (title.length <= 9) {
			return 48;
		} else if (title.length > 9 && title.length <= 12) {
			return 40;
		} else if (title.length > 12 && title.length <= 15) {
			return 30;
		}
	}

	detect(searchTerm: string, equipmentTypesList: Array<string>): Array<string> {
		switch (searchTerm.toLowerCase()) {
			case 'v':
				return this.updateTrailerType(TRAILER_TYPES_DB.VAN.toUpperCase(), equipmentTypesList);
			case 'f':
				return this.updateTrailerType(TRAILER_TYPES_DB.FLATBED.toUpperCase(), equipmentTypesList);
			case 'r':
				return this.updateTrailerType(TRAILER_TYPES_DB.REEFER.toUpperCase(), equipmentTypesList);
			case 'p':
				return this.updateTrailerType('POWER ONLY', equipmentTypesList);
			case 's':
				return this.updateTrailerType(TRAILER_TYPES_DB.STEPDECK.toUpperCase(), equipmentTypesList);
			case 'c':
				return this.updateTrailerType(TRAILER_TYPES_DB.CONTAINER.toUpperCase(), equipmentTypesList);
			case 'd':
				return this.updateTrailerType('DOUBLE DROP', equipmentTypesList);
			case 'g':
				return this.updateTrailerType(TRAILER_TYPES_DB.RGN.toUpperCase(), equipmentTypesList);
		}
	}

	updateTrailerType(trailerType: string, equipmentTypesList: Array<string>): Array<string> {
		if (equipmentTypesList.indexOf(trailerType) > -1) {
			equipmentTypesList = equipmentTypesList.filter((equipment) => equipment !== trailerType);
		} else {
			equipmentTypesList.push(trailerType);
		}
		return equipmentTypesList;
	}

	threePLIntegrationMissingClientData(integrationData: any) {
		const integrationDataJSON = JSON.parse(integrationData['data']);
		return (
			IS_EMPTY(integrationDataJSON['clientId']) || IS_EMPTY(integrationDataJSON['clientSecret'])
		);
	}

	isUserRole(role: string): boolean {
		return (
			!this.isNullOrUndefined(this.cargochief.getUser()) &&
			this.cargochief.getUser().role.split(',').indexOf(role) !== -1
		);
	}

	removeSpaceFromInputText(event: any, control: FormControl, str: string = null) {
		if (!str) {
			str = event.clipboardData.getData('text/plain');
		}
		str = str.replace(/\s/g, '');
		str = str.replace(/\t/g, '');
		str = str
			.toString()
			.trim()
			.replace(/(\r\n|\n|\r)/g, '');
		control.setValue(str);
		event.preventDefault();
	}

	formatTime(timeString) {
		const [hourString, minute] = timeString.split(':');
		const hour = +hourString % 24;
		return (hour % 12 || 12) + ':' + minute + (hour < 12 ? 'AM' : 'PM');
	}
}
